/**
 * Shopify Pricing Calculator - Vanilla JavaScript
 * Full feature parity with React version
 */

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const HOURLY_RATES = {
  "USA": { developer: 40, designer: 35, projectManager: 30, qualityAssurance: 25, writer: 20, creative: 25 },
  "Canada": { developer: 38, designer: 33, projectManager: 28, qualityAssurance: 23, writer: 18, creative: 23 },
  "UK": { developer: 42, designer: 37, projectManager: 32, qualityAssurance: 27, writer: 22, creative: 27 },
  "Australia": { developer: 45, designer: 40, projectManager: 35, qualityAssurance: 30, writer: 25, creative: 30 },
  "Europe": { developer: 35, designer: 30, projectManager: 25, qualityAssurance: 20, writer: 15, creative: 20 },
  "India": { developer: 15, designer: 12, projectManager: 10, qualityAssurance: 8, writer: 6, creative: 8 },
  "Southeast Asia": { developer: 18, designer: 15, projectManager: 12, qualityAssurance: 10, writer: 8, creative: 10 }
};

const THEME_HOURS = {
  "Basic Theme": 10,
  "Medium Customization": 20,
  "Heavy Customization": 40,
  "Fully Custom Theme": 90,
  "Enterprise Design System": 120
};

const COMPLEXITY_MULTIPLIERS = {
  "Basic": 1,
  "Moderate": 1.5,
  "Advanced": 2.2,
  "Highly Custom": 3.5
};

const TIMELINE_MULTIPLIERS = {
  "Standard": 1,
  "Fast-track": 1.3,
  "Ultra Urgent": 1.55
};

const PRODUCT_PAGE_HOURS = {
  "None": 0,
  "Basic": 15,
  "Advanced": 25,
  "Premium": 40
};

const SHOPIFY_PLUS_MULTIPLIER = 1.5;

// ============================================
// STATE MANAGEMENT
// ============================================

const state = {
  currentStep: 1,
  totalSteps: 6,
  formData: {
    shopifyPlan: "Shopify",
    theme: "Basic Theme",
    numberOfPages: 5,
    numberOfUniquePages: 3,
    numberOfSKUs: 50,
    features: [],
    storeSetup: false,
    paymentGateway: false,
    shippingLogistics: false,
    appIntegrations: [],
    productMigrationCount: 0,
    collectionSetupCount: 0,
    customMegaMenu: false,
    landingPageProducts: 0,
    productPageComplexity: "None",
    speedOptimization: false,
    croSetup: false,
    abTestingSetup: false,
    multiRegionSetup: false,
    timeline: "Standard",
    location: "USA"
  },
  rates: { ...HOURLY_RATES["USA"] },
  result: null
};

// ============================================
// INITIALIZATION
// ============================================

function init() {
  attachGlobalEventListeners();
  renderCurrentStep();
  updateProgressBar();
  calculateAndUpdateLivePrice();
}

// ============================================
// EVENT LISTENERS
// ============================================

function attachGlobalEventListeners() {
  document.addEventListener('click', (e) => {
    if (e.target.classList.contains('next-step-btn')) {
      e.preventDefault();
      nextStep();
    }
    if (e.target.classList.contains('prev-step-btn')) {
      e.preventDefault();
      prevStep();
    }
    if (e.target.classList.contains('add-feature-btn')) {
      e.preventDefault();
      addFeature();
    }
    if (e.target.classList.contains('remove-feature-btn')) {
      e.preventDefault();
      const index = parseInt(e.target.dataset.index);
      removeFeature(index);
    }
    if (e.target.classList.contains('add-app-btn')) {
      e.preventDefault();
      addAppIntegration();
    }
    if (e.target.classList.contains('remove-app-btn')) {
      e.preventDefault();
      const index = parseInt(e.target.dataset.index);
      removeAppIntegration(index);
    }
    if (e.target.id === 'exportBtn') {
      e.preventDefault();
      handleExport();
    }
    if (e.target.id === 'modifyEstimateBtn') {
      e.preventDefault();
      state.currentStep = 1;
      renderCurrentStep();
      updateProgressBar();
    }
    if (e.target.id === 'startOverBtn') {
      e.preventDefault();
      location.reload();
    }
  });

  document.addEventListener('change', (e) => {
    handleInputChange(e);
  });

  document.addEventListener('input', (e) => {
    if (e.target.type === 'number' || e.target.type === 'text') {
      handleInputChange(e);
    }
    // Handle rate changes
    if (e.target.classList.contains('rate-input')) {
      const role = e.target.dataset.role;
      const value = parseInt(e.target.value) || 0;
      state.rates[role] = value;
      calculateAndUpdateLivePrice();
    }
  });
}

function handleInputChange(e) {
  const target = e.target;
  const name = target.name;
  const value = target.type === 'checkbox' ? target.checked : 
                target.type === 'number' ? parseInt(target.value) || 0 : 
                target.value;

  if (name) {
    // Handle nested feature complexity
    if (name.startsWith('featureComplexity-')) {
      const index = parseInt(name.split('-')[1]);
      state.formData.features[index].complexity = value;
    }
    // Handle nested feature name
    else if (name.startsWith('featureName-')) {
      const index = parseInt(name.split('-')[1]);
      state.formData.features[index].name = value;
    }
    // Handle app integration name
    else if (name.startsWith('appName-')) {
      const index = parseInt(name.split('-')[1]);
      state.formData.appIntegrations[index].name = value;
    }
    // Handle app integration hours
    else if (name.startsWith('appHours-')) {
      const index = parseInt(name.split('-')[1]);
      state.formData.appIntegrations[index].hours = parseInt(value) || 0;
    }
    // Handle location change
    else if (name === 'location') {
      state.formData.location = value;
      state.rates = { ...HOURLY_RATES[value] };
      updateRateInputs();
    }
    // Regular form fields
    else {
      state.formData[name] = value;
      
      // Re-render step for radio button selections to show visual feedback immediately
      if (target.type === 'radio' && ['shopifyPlan', 'theme', 'productPageComplexity', 'timeline'].includes(name)) {
        renderCurrentStep();
      }
    }
    
    calculateAndUpdateLivePrice();
  }
}

function updateRateInputs() {
  const rateInputs = document.querySelectorAll('.rate-input');
  rateInputs.forEach(input => {
    const role = input.dataset.role;
    if (state.rates[role] !== undefined) {
      input.value = state.rates[role];
    }
  });
}

// ============================================
// NAVIGATION
// ============================================

function nextStep() {
  if (state.currentStep < state.totalSteps) {
    state.currentStep++;
    renderCurrentStep();
    updateProgressBar();
    calculateAndUpdateLivePrice();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function prevStep() {
  if (state.currentStep > 1) {
    state.currentStep--;
    renderCurrentStep();
    updateProgressBar();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function updateProgressBar() {
  const progressBar = document.getElementById('progressBar');
  const stepIndicator = document.getElementById('stepIndicator');
  const prevBtn = document.querySelector('.prev-step-btn');
  const nextBtn = document.querySelector('.next-step-btn');
  const curstep = state.currentStep;
  $('.progress-inner').removeClass("curr");
  $('.progress-inner').removeClass("completed");
  $('.calculator-layout').removeClass("hide-price-sidebar");

  if(curstep == 1){
    $('.progress-inner.one').addClass("curr");
  }
  else if(curstep == 2){
    $('.progress-inner.one').addClass("completed");
    $('.progress-inner.two').addClass("curr");
  }
  else if(curstep == 3){
    $('.progress-inner.one').addClass("completed");
    $('.progress-inner.two').addClass("completed");
    $('.progress-inner.three').addClass("curr");
  }
  else if(curstep == 4){
    $('.progress-inner.one').addClass("completed");
    $('.progress-inner.two').addClass("completed");
    $('.progress-inner.three').addClass("completed");
    $('.progress-inner.four').addClass("curr");
  }
  else if(curstep == 5){
    $('.progress-inner.one').addClass("completed");
    $('.progress-inner.two').addClass("completed");
    $('.progress-inner.three').addClass("completed");
    $('.progress-inner.four').addClass("completed");
    $('.progress-inner.five').addClass("curr");
  }
  else if(curstep == 6){
    $('.progress-inner.one').addClass("completed");
    $('.progress-inner.two').addClass("completed");
    $('.progress-inner.three').addClass("completed");
    $('.progress-inner.four').addClass("completed");
    $('.progress-inner.five').addClass("completed");
    $('.progress-inner.six').addClass("curr");
    $('.calculator-layout').addClass("hide-price-sidebar");
  }

  const progress = (state.currentStep / state.totalSteps) * 100;
  progressBar.style.width = progress + '%';
  stepIndicator.textContent = `Step ${state.currentStep} of ${state.totalSteps}`;

  // Show/hide navigation buttons
  prevBtn.style.display = state.currentStep > 1 ? 'inline-block' : 'none';
  nextBtn.style.display = state.currentStep < state.totalSteps ? 'inline-block' : 'none';
}

// ============================================
// STEP RENDERING
// ============================================

function renderCurrentStep() {
  const stepContent = document.getElementById('stepContent');
  
  switch (state.currentStep) {
    case 1:
      stepContent.innerHTML = renderStep1();
      break;
    case 2:
      stepContent.innerHTML = renderStep2();
      break;
    case 3:
      stepContent.innerHTML = renderStep3();
      break;
    case 4:
      stepContent.innerHTML = renderStep4();
      break;
    case 5:
      stepContent.innerHTML = renderStep5();
      break;
    case 6:
      calculatePricing();
      stepContent.innerHTML = renderStep6();
      break;
  }
}

function renderStep1() {
  return `
    <div class="step-content">
      <h2 class="step-title">Select Your Shopify Plan</h2>
      <p class="step-description">Choose the Shopify plan that best fits your business needs</p>
      
      <div class="plan-cards">
        <label class="plan-card ${state.formData.shopifyPlan === 'Shopify' ? 'selected' : ''}">
          <input type="radio" name="shopifyPlan" value="Shopify" ${state.formData.shopifyPlan === 'Shopify' ? 'checked' : ''}>
          <div class="plan-card-content">
          <div class="my-cust-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-store w-8 h-8 text-muted-foreground"><path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"></path><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"></path><path d="M2 7h20"></path><path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7"></path></svg>
          </div>
            <h3>Shopify</h3>
            <p>Perfect for small to medium businesses</p>
          </div>
        </label>
        
        <label class="plan-card ${state.formData.shopifyPlan === 'Shopify Advanced' ? 'selected' : ''}">
          <input type="radio" name="shopifyPlan" value="Shopify Advanced" ${state.formData.shopifyPlan === 'Shopify Advanced' ? 'checked' : ''}>
          <div class="plan-card-content">
          <div class="my-cust-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-zap w-8 h-8 text-muted-foreground"><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path></svg>
          </div>
            <h3>Shopify Advanced</h3>
            <p>Advanced features for growing businesses</p>
          </div>
        </label>
        
        <label class="plan-card ${state.formData.shopifyPlan === 'Shopify Plus' ? 'selected' : ''}">
          <input type="radio" name="shopifyPlan" value="Shopify Plus" ${state.formData.shopifyPlan === 'Shopify Plus' ? 'checked' : ''}>
          <div class="plan-card-content">
          <div class="my-cust-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-crown w-8 h-8 text-muted-foreground"><path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"></path><path d="M5 21h14"></path></svg>
          </div>
            <h3>Shopify Plus</h3>
            <p>Enterprise-level solution (+50% premium)</p>
          </div>
        </label>
      </div>
    </div>
  `;
}

function renderStep2() {
  return `
    <div class="step-content">
      <h2 class="step-title">Choose Your Theme Level</h2>
      <p class="step-description">Select the level of customization for your store's design</p>
      
      <div class="theme-cards">
        ${Object.keys(THEME_HOURS)
  .map((theme, index) => {
    let extraHtml = "";
    let extraBottomHtml = "";
    if (index === 0) {
      extraHtml = `<div class="my-cust-icon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-palette w-6 h-6 text-muted-foreground"><circle cx="13.5" cy="6.5" r=".5" fill="currentColor"></circle><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"></circle><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"></circle><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"></circle><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"></path></svg></div>`;
      extraBottomHtml = `<div class="desc">Pre-built theme with minor tweaks</div>`;
    } else if (index === 1) {
      extraHtml = `<div class="my-cust-icon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-brush w-6 h-6 text-muted-foreground"><path d="m9.06 11.9 8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08"></path><path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z"></path></svg></div>`;
      extraBottomHtml = `<div class="desc">Moderate theme customization</div>`;
    } else if (index === 2) {
      extraHtml = `<div class="my-cust-icon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-wand-sparkles w-6 h-6 text-muted-foreground"><path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72"></path><path d="m14 7 3 3"></path><path d="M5 6v4"></path><path d="M19 14v4"></path><path d="M10 2v2"></path><path d="M7 8H3"></path><path d="M21 16h-4"></path><path d="M11 3H9"></path></svg></div>`;
      extraBottomHtml = `<div class="desc">Extensive theme customization</div>`;
    } else if (index === 3) {
      extraHtml = `<div class="my-cust-icon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-sparkles w-6 h-6 text-muted-foreground"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path><path d="M20 3v4"></path><path d="M22 5h-4"></path><path d="M4 17v2"></path><path d="M5 18H3"></path></svg></div>`;
      extraBottomHtml = `<div class="desc">Bespoke theme built from scratch</div>`;
    } else if (index === 4) {
      extraHtml = `<div class="my-cust-icon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trophy w-6 h-6 text-muted-foreground"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path></svg></div>`;
      extraBottomHtml = `<div class="desc">Complete custom design ecosystem</div>`;
    }

    return `
      <label class="theme-card ${state.formData.theme === theme ? 'selected' : ''}">
        <input 
          type="radio" 
          name="theme" 
          value="${theme}" 
          ${state.formData.theme === theme ? 'checked' : ''}
        />
        <div class="theme-card-content">
          <div class="inner-theme-cont">
            ${extraHtml}
            <span class="theme-hours">${THEME_HOURS[theme]} hours</span>
          </div>
          <h3>${theme}</h3>
          ${extraBottomHtml}
        </div>
      </label>
    `;
  })
  .join('')}


      </div>
    </div>
  `;
}

function renderStep3() {
  return `
    <div class="step-content">
      <h2 class="step-title">Setup & Integration</h2>
      <p class="step-description">Configure your store setup and integrations</p>
      
      <!-- Store Scope -->
      <div class="form-section">
        <h3 class="section-title">Store Scope</h3>
        <div class="form-grid">
          <div class="form-group">
            <label>Number of Pages</label>
            <input type="number" name="numberOfPages" value="${state.formData.numberOfPages}" min="1" class="form-input">
          </div>
          <div class="form-group">
            <label>Number of Unique Page Designs</label>
            <input type="number" name="numberOfUniquePages" value="${state.formData.numberOfUniquePages}" min="1" class="form-input">
          </div>
          <div class="form-group">
            <label>Number of SKUs</label>
            <input type="number" name="numberOfSKUs" value="${state.formData.numberOfSKUs}" min="0" class="form-input">
          </div>
        </div>
      </div>

      <!-- Basic Setup -->
      <div class="form-section">
        <h3 class="section-title">Basic Setup</h3>
        <div class="checkbox-group">
          <label class="checkbox-label toggle">
            <input class="toggle-checkbox" type="checkbox" name="storeSetup" ${state.formData.storeSetup ? 'checked' : ''}>
            <div class="toggle-switch"></div>
            <span>Store Setup (2 hours)</span>
          </label>
          <label class="checkbox-label toggle">
            <input class="toggle-checkbox" type="checkbox" name="paymentGateway" ${state.formData.paymentGateway ? 'checked' : ''}>
            <div class="toggle-switch"></div>
            <span>Payment Gateway Integration (4 hours)</span>
          </label>
          <label class="checkbox-label toggle">
            <input class="toggle-checkbox" type="checkbox" name="shippingLogistics" ${state.formData.shippingLogistics ? 'checked' : ''}>
            <div class="toggle-switch"></div>
            <span>Shipping & Logistics Setup (7 hours)</span>
          </label>
        </div>
      </div>

      <!-- App Integrations -->
      <div class="form-section">
      <div class="head-add-btnn">
        <h3 class="section-title">App Integrations</h3>
        <button type="button" class="add-app-btn btn-secondary">+ Add App</button>
        </div>
        <div id="appIntegrationsList" class="dynamic-list">
          ${state.formData.appIntegrations.map((app, index) => `
            <div class="dynamic-item" data-index="${index}">
              <div class="dynamic-item-fields">
                <input type="text" name="appName-${index}" value="${app.name || ''}" placeholder="App name" class="form-input">
                <input type="number" name="appHours-${index}" value="${app.hours || 1}" min="1" max="10" placeholder="Hours" class="form-input small-input">
              </div>
              <button type="button" class="icon-btn remove-app-btn" data-index="${index}">
                <span><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trash2 w-4 h-4"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" x2="10" y1="11" y2="17"></line><line x1="14" x2="14" y1="11" y2="17"></line></svg></span>
              </button>
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Migration & Collections -->
      <div class="form-section">
        <h3 class="section-title">Migration & Collections</h3>
        <div class="form-grid">
          <div class="form-group">
            <label>Product Migration Count</label>
            <input type="number" name="productMigrationCount" value="${state.formData.productMigrationCount}" min="0" class="form-input">
            <small>0.1 hours per product</small>
          </div>
          <div class="form-group">
            <label>Collection Setup Count</label>
            <input type="number" name="collectionSetupCount" value="${state.formData.collectionSetupCount}" min="0" class="form-input">
            <small>1 hour per collection</small>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderStep4() {
  return `
    <div class="step-content">
      <h2 class="step-title">Define Your Features</h2>
      <p class="step-description">Add features one by one with their complexity levels</p>
      <div class="head-feature-sec">
      <div class="head-feature-inner">
        <h2 class="step-title">Define Your Features</h2>
        <p class="help-text">Each feature's hours = base hours × complexity multiplier</p>
        </div>
        <button type="button" class="add-feature-btn btn-secondary">+ Add Feature</button>
      </div>
      
      <div class="form-section">
        <div id="featuresList" class="dynamic-list">
          ${state.formData.features.map((feature, index) => `
            <div class="dynamic-item feature-item" data-index="${index}">
              <div class="dynamic-item-fields">
              <div class="dynmic-first-field">
                <label>Feature Name</label>
                <input type="text" name="featureName-${index}" value="${feature.name || ''}" placeholder="e.g., Product Quick View, Custom Wishlist" class="form-input">
              </div>
              <div class="dynmic-second-field">
              <label>Complexity Level</label>
              <div class="sidebar-select-cont">
                <select name="featureComplexity-${index}" class="form-select">
                  <option value="Basic" ${feature.complexity === 'Basic' ? 'selected' : ''}>Basic</option>
                  <option value="Moderate" ${feature.complexity === 'Moderate' ? 'selected' : ''}>Moderate</option>
                  <option value="Advanced" ${feature.complexity === 'Advanced' ? 'selected' : ''}>Advanced</option>
                  <option value="Highly Custom" ${feature.complexity === 'Highly Custom' ? 'selected' : ''}>Highly Custom</option>
                </select>
                </div>
                </div>
              </div>
              <button type="button" class="icon-btn remove-feature-btn" data-index="${index}">
                <span><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trash2 w-4 h-4"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" x2="10" y1="11" y2="17"></line><line x1="14" x2="14" y1="11" y2="17"></line></svg></span>
              </button>
            </div>
          `).join('')}
        </div>
      </div>
    </div>
  `;
}

function renderStep5() {
  return `
    <div class="step-content">
      <h2 class="step-title">Advanced Modules</h2>
      <p class="step-description">Select premium features to enhance your store</p>
      
      <div class="form-section">
        <div class="checkbox-group">
          <label class="checkbox-label toggle">
            <input class="toggle-checkbox" type="checkbox" name="customMegaMenu" ${state.formData.customMegaMenu ? 'checked' : ''}>
            <div class="toggle-switch"></div>
            <span>Custom Mega Menu (8 hours)</span>
          </label>
          <label class="checkbox-label toggle">
            <input class="toggle-checkbox" type="checkbox" name="speedOptimization" ${state.formData.speedOptimization ? 'checked' : ''}>
            <div class="toggle-switch"></div>
            <span>Speed Optimization (12 hours)</span>
          </label>
          <label class="checkbox-label toggle">
            <input class="toggle-checkbox" type="checkbox" name="croSetup" ${state.formData.croSetup ? 'checked' : ''}>
            <div class="toggle-switch"></div>
            <span>CRO Setup (20 hours)</span>
          </label>
          <label class="checkbox-label toggle">
            <input class="toggle-checkbox" type="checkbox" name="abTestingSetup" ${state.formData.abTestingSetup ? 'checked' : ''}>
            <div class="toggle-switch"></div>
            <span>A/B Testing Setup (10 hours)</span>
          </label>
          <label class="checkbox-label toggle">
            <input class="toggle-checkbox" type="checkbox" name="multiRegionSetup" ${state.formData.multiRegionSetup ? 'checked' : ''}>
            <div class="toggle-switch"></div>
            <span>Multi-region Setup (12 hours)</span>
          </label>
        </div>
      </div>

      <div class="form-section">
        <h3 class="section-title">Landing Page Products</h3>
        <div class="form-group">
          <label>Number of Products</label>
          <input type="number" name="landingPageProducts" value="${state.formData.landingPageProducts}" min="0" class="form-input">
          <small>1 hour per product</small>
        </div>
      </div>

      <div class="form-section">
        <h3 class="section-title">Product Page Complexity</h3>
        <div class="timeline-cards">
          ${Object.keys(PRODUCT_PAGE_HOURS).map(level => `
            <label class="timeline-card ${state.formData.productPageComplexity === level ? 'selected' : ''}">
              <input type="radio" name="productPageComplexity" value="${level}" ${state.formData.productPageComplexity === level ? 'checked' : ''}>
              <div class="timeline-card-content">
                <h3>${level}</h3>
              </div>
            </label>
          `).join('')}
        </div>
      </div>

      <div class="form-section">
        <h3 class="section-title">Project Timeline</h3>
        <p class="step-description">Select your desired timeline</p>
        <div class="timeline-cards">
          ${Object.keys(TIMELINE_MULTIPLIERS).map(timeline => `
            <label class="timeline-card ${state.formData.timeline === timeline ? 'selected' : ''}">
              <input type="radio" name="timeline" value="${timeline}" ${state.formData.timeline === timeline ? 'checked' : ''}>
              <div class="timeline-card-content">
                <h3>${timeline}</h3>
                <span>×${TIMELINE_MULTIPLIERS[timeline]}</span>
              </div>
            </label>
          `).join('')}
        </div>
      </div>
    </div>
  `;
}

function renderStep6() {
  if (!state.result) return '<p>Calculating...</p>';
  
  const result = state.result;
  
  return `
    <div id="pdfResultsContent" class="results-container">
      <div class="results-header">
        <h2 class="results-title">Your Custom Estimate</h2>
        <p class="results-subtitle">Professional pricing breakdown for your Shopify project</p>
      </div>

      <!-- Project Brief -->
      <div class="results-card">
        <h3 class="card-title">📋 Project Brief</h3>
        
        <div class="brief-section">
          <h4>Basic Configuration</h4>
          <div class="brief-grid">
            <div class="brief-item">
              <span class="brief-label">Shopify Plan</span>
              <span class="brief-value">${state.formData.shopifyPlan}</span>
            </div>
            <div class="brief-item">
              <span class="brief-label">Theme</span>
              <span class="brief-value">${state.formData.theme}</span>
            </div>
            <div class="brief-item">
              <span class="brief-label">Timeline</span>
              <span class="brief-value">${state.formData.timeline}</span>
            </div>
          </div>
        </div>

        <div class="brief-section">
          <h4>Store Scope</h4>
          <div class="brief-grid">
            <div class="brief-item">
              <span class="brief-label">Total Pages</span>
              <span class="brief-value">${state.formData.numberOfPages}</span>
            </div>
            <div class="brief-item">
              <span class="brief-label">Unique Designs</span>
              <span class="brief-value">${state.formData.numberOfUniquePages}</span>
            </div>
            <div class="brief-item">
              <span class="brief-label">Total SKUs</span>
              <span class="brief-value">${state.formData.numberOfSKUs}</span>
            </div>
          </div>
        </div>

        ${state.formData.features.length > 0 ? `
          <div class="brief-section">
            <h4>Custom Features (${state.formData.features.length})</h4>
            <div class="feature-list">
              ${state.formData.features.map(f => `
                <div class="feature-list-item">
                  <span>${f.name || 'Custom Feature'}</span>
                  <span class="feature-complexity">${f.complexity}</span>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}

        ${state.formData.appIntegrations.length > 0 ? `
          <div class="brief-section">
            <h4>App Integrations (${state.formData.appIntegrations.length})</h4>
            <div class="feature-list">
              ${state.formData.appIntegrations.map(app => `
                <div class="feature-list-item">
                  <span>${app.name || 'App Integration'}</span>
                  <span class="feature-complexity">${app.hours} hrs</span>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}

        ${(state.formData.customMegaMenu || state.formData.landingPageProducts > 0 || state.formData.productPageComplexity !== 'None' || 
           state.formData.speedOptimization || state.formData.croSetup || state.formData.abTestingSetup || state.formData.multiRegionSetup) ? `
          <div class="brief-section">
            <h4>Advanced Modules</h4>
            <div class="feature-list">
              ${state.formData.customMegaMenu ? '<div class="feature-list-item"><span>Custom Mega Menu</span></div>' : ''}
              ${state.formData.landingPageProducts > 0 ? `<div class="feature-list-item"><span>Landing Page Products</span><span class="feature-complexity">${state.formData.landingPageProducts} products</span></div>` : ''}
              ${state.formData.productPageComplexity !== 'None' ? `<div class="feature-list-item"><span>Product Page Complexity</span><span class="feature-complexity">${state.formData.productPageComplexity}</span></div>` : ''}
              ${state.formData.speedOptimization ? '<div class="feature-list-item"><span>Speed Optimization</span></div>' : ''}
              ${state.formData.croSetup ? '<div class="feature-list-item"><span>CRO Setup</span></div>' : ''}
              ${state.formData.abTestingSetup ? '<div class="feature-list-item"><span>A/B Testing Setup</span></div>' : ''}
              ${state.formData.multiRegionSetup ? '<div class="feature-list-item"><span>Multi-region Setup</span></div>' : ''}
            </div>
          </div>
        ` : ''}
      </div>

      <!-- Total Investment -->
      <div class="results-card price-card">
        <div class="price-header">
          <h3 class="card-title">💰 Total Investment</h3>
          <span class="location-badge">${state.formData.location}</span>
        </div>
        <div class="price-display">
          <div class="price-main">
            <span class="price-label">Total Project Investment</span>
            <span class="price-value">$${formatNumber(result.totalUSD)}</span>
          </div>
          <div class="price-stats">
            <div class="price-stat">
              <span class="stat-value">${result.totalHours.toFixed(0)}</span>
              <span class="stat-label">Total Hours</span>
            </div>
            <div class="price-stat">
              <span class="stat-value">${result.estimatedWeeks}w</span>
              <span class="stat-label">Timeline</span>
            </div>
            <div class="price-stat">
              <span class="stat-value">6</span>
              <span class="stat-label">Resources</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Hour Distribution -->
      <div class="results-card">
        <h3 class="card-title">Hour Distribution by Category</h3>
        <div class="breakdown-list">
          ${result.hourBreakdown.theme > 0 ? `
            <div class="breakdown-item">
              <span class="breakdown-label">Theme Customization</span>
              <span class="breakdown-value">${result.hourBreakdown.theme.toFixed(1)} hrs</span>
            </div>
          ` : ''}
          ${result.hourBreakdown.features > 0 ? `
            <div class="breakdown-item">
              <span class="breakdown-label">Features Development</span>
              <span class="breakdown-value">${result.hourBreakdown.features.toFixed(1)} hrs</span>
            </div>
          ` : ''}
          ${result.hourBreakdown.setup > 0 ? `
            <div class="breakdown-item">
              <span class="breakdown-label">Setup & Integrations</span>
              <span class="breakdown-value">${result.hourBreakdown.setup.toFixed(1)} hrs</span>
            </div>
          ` : ''}
          ${result.hourBreakdown.advancedModules > 0 ? `
            <div class="breakdown-item">
              <span class="breakdown-label">Advanced Modules</span>
              <span class="breakdown-value">${result.hourBreakdown.advancedModules.toFixed(1)} hrs</span>
            </div>
          ` : ''}
        </div>
      </div>

      <!-- Resource Allocation -->
      <div class="results-card">
        <h3 class="card-title">Resource Allocation & Investments</h3>
        <div class="resource-list">
          <div class="resource-item">
            <div class="resource-info">
              <span class="resource-name">Developer</span>
              <span class="resource-rate">$${state.rates.developer}/hr</span>
            </div>
            <div class="resource-allocation">
              <span class="resource-hours">${result.distributedHours.developer.toFixed(1)} hrs</span>
              <span class="resource-percentage">(${((result.distributedHours.developer / result.totalHours) * 100).toFixed(0)}%)</span>
            </div>
          </div>
          <div class="resource-item">
            <div class="resource-info">
              <span class="resource-name">Designer</span>
              <span class="resource-rate">$${state.rates.designer}/hr</span>
            </div>
            <div class="resource-allocation">
              <span class="resource-hours">${result.distributedHours.designer.toFixed(1)} hrs</span>
              <span class="resource-percentage">(${((result.distributedHours.designer / result.totalHours) * 100).toFixed(0)}%)</span>
            </div>
          </div>
          <div class="resource-item">
            <div class="resource-info">
              <span class="resource-name">Project Manager</span>
              <span class="resource-rate">$${state.rates.projectManager}/hr</span>
            </div>
            <div class="resource-allocation">
              <span class="resource-hours">${result.distributedHours.projectManager.toFixed(1)} hrs</span>
              <span class="resource-percentage">(${((result.distributedHours.projectManager / result.totalHours) * 100).toFixed(0)}%)</span>
            </div>
          </div>
          <div class="resource-item">
            <div class="resource-info">
              <span class="resource-name">Quality Assurance</span>
              <span class="resource-rate">$${state.rates.qualityAssurance}/hr</span>
            </div>
            <div class="resource-allocation">
              <span class="resource-hours">${result.distributedHours.qualityAssurance.toFixed(1)} hrs</span>
              <span class="resource-percentage">(${((result.distributedHours.qualityAssurance / result.totalHours) * 100).toFixed(0)}%)</span>
            </div>
          </div>
          <div class="resource-item">
            <div class="resource-info">
              <span class="resource-name">Content Writer</span>
              <span class="resource-rate">$${state.rates.writer}/hr</span>
            </div>
            <div class="resource-allocation">
              <span class="resource-hours">${result.distributedHours.writer.toFixed(1)} hrs</span>
              <span class="resource-percentage">(${((result.distributedHours.writer / result.totalHours) * 100).toFixed(0)}%)</span>
            </div>
          </div>
          <div class="resource-item">
            <div class="resource-info">
              <span class="resource-name">Brand Visual Creator</span>
              <span class="resource-rate">$${state.rates.creative}/hr</span>
            </div>
            <div class="resource-allocation">
              <span class="resource-hours">${result.distributedHours.creative.toFixed(1)} hrs</span>
              <span class="resource-percentage">(${((result.distributedHours.creative / result.totalHours) * 100).toFixed(0)}%)</span>
            </div>
          </div>
        </div>
      </div>

      
    </div>
    <!-- Action Buttons -->
      <div class="results-actions">
        <button id="exportBtn" class="btn-primary"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-download w-5 h-5 mr-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" x2="12" y1="15" y2="3"></line></svg>
         Download Estimate (PDF)</button>
        <button id="modifyEstimateBtn" class="btn-secondary">Modify Estimate</button>
        <button id="startOverBtn" class="btn-secondary"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-refresh-cw w-5 h-5 mr-2"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path><path d="M21 3v5h-5"></path><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path><path d="M8 16H3v5"></path></svg>
         Start Over</button>
      </div>
  `;
}

// ============================================
// FEATURE & APP MANAGEMENT
// ============================================

function addFeature() {
  state.formData.features.push({
    name: '',
    complexity: 'Basic'
  });
  renderCurrentStep();
  calculateAndUpdateLivePrice();
}

function removeFeature(index) {
  state.formData.features.splice(index, 1);
  renderCurrentStep();
  calculateAndUpdateLivePrice();
}

function addAppIntegration() {
  state.formData.appIntegrations.push({
    name: '',
    hours: 1
  });
  renderCurrentStep();
  calculateAndUpdateLivePrice();
}

function removeAppIntegration(index) {
  state.formData.appIntegrations.splice(index, 1);
  renderCurrentStep();
  calculateAndUpdateLivePrice();
}

// ============================================
// PRICING CALCULATION
// ============================================

function calculatePricing() {
  const formData = state.formData;
  const rates = state.rates;

  // Step 1: Theme Hours
  const themeHours = THEME_HOURS[formData.theme];

  // Step 2: Feature Hours (each feature has its own complexity)
  const featureHours = formData.features.reduce((total, feature) => {
    const complexityMultiplier = COMPLEXITY_MULTIPLIERS[feature.complexity];
    return total + complexityMultiplier;
  }, 0);

  // Step 3: Setup & Integration Hours
  const appIntegrationHours = formData.appIntegrations.reduce((total, app) => {
    return total + (app.hours || 0);
  }, 0);
  
  const pageSetupHours = formData.numberOfPages * 0.5;
  const uniquePageHours = formData.numberOfUniquePages * 2;
  const skuSetupHours = formData.numberOfSKUs * 0.05;
  
  const setupHours =
    (formData.storeSetup ? 2 : 0) +
    (formData.paymentGateway ? 4 : 0) +
    (formData.shippingLogistics ? 7 : 0) +
    appIntegrationHours +
    formData.productMigrationCount * 0.1 +
    formData.collectionSetupCount * 1 +
    pageSetupHours +
    uniquePageHours +
    skuSetupHours;

  // Step 4: Advanced Module Hours
  const advancedModuleHours =
    (formData.customMegaMenu ? 8 : 0) +
    (formData.landingPageProducts * 1) +
    PRODUCT_PAGE_HOURS[formData.productPageComplexity] +
    (formData.speedOptimization ? 12 : 0) +
    (formData.croSetup ? 20 : 0) +
    (formData.abTestingSetup ? 10 : 0) +
    (formData.multiRegionSetup ? 12 : 0);

  // Total Hours
  const totalHours = themeHours + featureHours + setupHours + advancedModuleHours;

  // Resource Distribution
  const distributedHours = {
    developer: totalHours * 0.55,
    designer: totalHours * 0.2,
    projectManager: totalHours * 0.1,
    qualityAssurance: totalHours * 0.05,
    writer: totalHours * 0.05,
    creative: totalHours * 0.05
  };

  // Resource Investments
  const resourceInvestments = {
    developer: distributedHours.developer * rates.developer,
    designer: distributedHours.designer * rates.designer,
    projectManager: distributedHours.projectManager * rates.projectManager,
    qualityAssurance: distributedHours.qualityAssurance * rates.qualityAssurance,
    writer: distributedHours.writer * rates.writer,
    creative: distributedHours.creative * rates.creative
  };

  const totalResourceInvestment = Object.values(resourceInvestments).reduce((sum, Investment) => sum + Investment, 0);

  // Apply Multipliers
  let totalUSD = totalResourceInvestment;

  // Shopify Plus multiplier
  const shopifyPlusMultiplier = formData.shopifyPlan === "Shopify Plus" ? 1.5 : 1;
  totalUSD *= shopifyPlusMultiplier;

  // Timeline multiplier
  const timelineMultiplier = TIMELINE_MULTIPLIERS[formData.timeline];
  totalUSD *= timelineMultiplier;

  // Estimated weeks - adjusted for timeline urgency
  const baseWeeks = totalHours / 40;
  const adjustedWeeks = baseWeeks / timelineMultiplier;
  const estimatedWeeks = adjustedWeeks < 1 ? Math.round(adjustedWeeks * 10) / 10 : Math.round(adjustedWeeks * 2) / 2;

  state.result = {
    totalHours,
    distributedHours,
    hourBreakdown: {
      theme: themeHours,
      features: featureHours,
      setup: setupHours,
      advancedModules: advancedModuleHours
    },
    resourceInvestments,
    totalUSD,
    estimatedWeeks,
    multipliers: {
      shopifyPlus: shopifyPlusMultiplier,
      timeline: timelineMultiplier
    }
  };

  return state.result;
}

function calculateAndUpdateLivePrice() {
  calculatePricing();
  updateLivePriceSidebar();
}

function updateLivePriceSidebar() {
  const sidebar = document.getElementById('livePriceSidebar');
  const livePrice = document.getElementById('livePrice');
  const liveHours = document.getElementById('liveHours');
  const liveWeeks = document.getElementById('liveWeeks');
  
  if (!state.result) return;

  // Hide price on steps 1 and 2
  const showPricing = state.currentStep > 2;
  
  if (showPricing) {
    livePrice.textContent = '$' + formatNumber(state.result.totalUSD);
    liveHours.textContent = state.result.totalHours.toFixed(0);
    liveWeeks.textContent = state.result.estimatedWeeks;
    
    // Show the price content
    const priceContent = sidebar.querySelector('.sidebar-price');
    const statsContent = sidebar.querySelector('.sidebar-stats');
    if (priceContent) priceContent.style.display = 'block';
    if (statsContent) statsContent.style.display = 'flex';
  } else {
    // Hide the price content
    const priceContent = sidebar.querySelector('.sidebar-price');
    const statsContent = sidebar.querySelector('.sidebar-stats');
    if (priceContent) priceContent.style.display = 'none';
    if (statsContent) statsContent.style.display = 'none';
  }
}

// ============================================
// EXPORT FUNCTIONALITY - PDF Download
// ============================================

async function handleExport() {
  // Check if required libraries are loaded
  if (typeof html2canvas === 'undefined' || typeof window.jspdf === 'undefined') {
    alert("PDF libraries are still loading. Please wait a moment and try again.");
    return;
  }

  const resultsContent = document.getElementById('pdfResultsContent');
  if (!resultsContent) {
    console.error('Results content not found');
    alert("Unable to find results content. Please ensure you're on the results page.");
    return;
  }

  // Find the export button and add loading state
  const exportBtn = document.querySelector('[data-action="export"]');
  const originalText = exportBtn ? exportBtn.textContent : '';
  if (exportBtn) {
    exportBtn.disabled = true;
    exportBtn.textContent = 'Generating PDF...';
  }

  try {
    const canvas = await html2canvas(resultsContent, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      windowWidth: 1200,
    });

    const imgData = canvas.toDataURL("image/png");
    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth - 20; // 10mm margins on each side
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    let heightLeft = imgHeight;
    let position = 10; // 10mm top margin

    // Add first page with margin
    pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
    heightLeft -= (pdfHeight - 20);

    // Add additional pages if content is too long
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + 10;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
      heightLeft -= (pdfHeight - 20);
    }

    pdf.save(`shopify-estimate-${Date.now()}.pdf`);
  } catch (error) {
    console.error("Error generating PDF:", error);
    alert("Error generating PDF. Please try again.");
  } finally {
    // Restore button state
    const exportBtn = document.querySelector('[data-action="export"]');
    if (exportBtn) {
      exportBtn.disabled = false;
      exportBtn.textContent = originalText;
    }
  }
}

// ============================================
// UTILITIES
// ============================================

function formatNumber(num) {
  return Math.round(num).toLocaleString('en-US');
}

// ============================================
// INITIALIZE ON DOM READY
// ============================================

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}


